import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatPrice, apiErr, API } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "paid", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
    const qc = useQueryClient();
    const [filter, setFilter] = useState("");
    const { data: orders = [] } = useQuery({ queryKey: ["admin-orders", filter], queryFn: async () => (await api.get("/orders", { params: filter ? { status: filter } : {} })).data });
    const [open, setOpen] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const { data: allProducts = [] } = useQuery({ queryKey: ["admin-products-for-orders"], queryFn: async () => (await api.get("/products/all-admin")).data });
    const skuMap = Object.fromEntries(allProducts.map((p) => [p.id, p.sku || ""]));

    useEffect(() => {
        const oid = searchParams.get("open");
        if (oid && orders.length) {
            const found = orders.find((o) => o.id === oid);
            if (found) setOpen(found);
            setSearchParams({}, { replace: true });
        }
    }, [orders]);

    const updateStatus = async (id, status) => {
        try { await api.patch(`/orders/${id}`, { status, payment_status: status === "paid" ? "paid" : undefined }); toast.success("Actualizado"); qc.invalidateQueries({ queryKey: ["admin-orders"] }); }
        catch (e) { toast.error(apiErr(e)); }
    };
    const updatePayment = async (id, payment_status) => {
        try {
            await api.patch(`/orders/${id}`, { payment_status, status: payment_status === "paid" ? "paid" : "pending" });
            toast.success(payment_status === "paid" ? "Pago aprobado" : payment_status === "rejected" ? "Pago desaprobado" : "Pago vuelto a verificación");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
            setOpen(null);
        } catch (e) { toast.error(apiErr(e)); }
    };
    const openReceipt = async (oid) => {
        try {
            const token = localStorage.getItem("jm_token");
            const r = await fetch(`${API}/orders/${oid}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error("No se pudo cargar el comprobante");
            window.open(URL.createObjectURL(await r.blob()), "_blank");
        } catch (e) { toast.error(e.message); }
    };
    const deleteOrder = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar este pedido? Esta acción no se puede deshacer.")) return;
        try {
            await api.delete(`/orders/${id}`);
            toast.success("Pedido eliminado");
            qc.invalidateQueries({ queryKey: ["admin-orders"] });
            qc.invalidateQueries({ queryKey: ["admin-orders-recent"] });
            setOpen(null);
        } catch (e) { toast.error(apiErr(e)); }
    };
    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-display text-3xl">Pedidos</h1>
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border border-sandLight bg-white rounded-md px-3 py-2 text-sm">
                    <option value="">Todos</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div className="bg-white border border-sandLight rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-bone text-left text-xs uppercase text-ink/60"><tr><th className="p-3">ID</th><th>Cliente</th><th>Total</th><th>Estado</th><th>Método</th><th>Fecha</th></tr></thead>
                    <tbody>
                        {orders.map((o) => (
                            <tr key={o.id} className="border-t border-sandLight cursor-pointer hover:bg-bone/50" onClick={() => setOpen(o)}>
                                <td className="p-3">#{o.id.slice(0, 8)}</td>
                                <td>{o.customer?.name}<br /><span className="text-xs text-ink/50">{o.customer?.email}</span></td>
                                <td>{formatPrice(o.total)}</td>
                                <td><span className="text-xs uppercase">{o.status}</span></td>
                                <td className="text-xs">{o.payment_method}<br /><span className={`${o.payment_status === "verifying" ? "text-terracotta font-medium" : "text-ink/50"}`}>{o.payment_status}</span></td>
                                <td className="text-xs">{new Date(o.created_at).toLocaleString("es-CO")}</td>
                            </tr>
                        ))}
                        {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-ink/50">Sin pedidos</td></tr>}
                    </tbody>
                </table>
            </div>
            {open && (
                <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-sandLight">
                            <h2 className="font-display text-2xl">Pedido #{open.id.slice(0, 8)}</h2>
                            <p className="text-xs text-ink/60 mt-1">{new Date(open.created_at).toLocaleString("es-CO")}</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div><p className="text-eyebrow">Cliente</p><p>{open.customer?.name}</p><p className="text-xs text-ink/60">{open.customer?.email}</p><p className="text-xs text-ink/60">{open.customer?.phone}</p></div>
                                <div><p className="text-eyebrow">Envío</p><p className="text-xs">{open.customer?.address}</p><p className="text-xs">{open.customer?.city}</p>{open.customer?.notes && <p className="text-xs text-ink/60 mt-1">Notas: {open.customer.notes}</p>}</div>
                            </div>
                            <div>
                                <p className="text-eyebrow mb-2">Items</p>
                                <div className="divide-y divide-sandLight border-y border-sandLight text-sm">
                                    {open.items.map((i, k) => {
                                        const itemCode = i.sku || skuMap[i.productId];
                                        return (
                                        <div key={k} className="py-2 flex justify-between">
                                            <span>{i.name} · {i.size} · {i.color}{itemCode ? ` · ITEM: ${itemCode}` : ""} × {i.quantity}</span>
                                            <span>{formatPrice(i.price * i.quantity)}</span>
                                        </div>
                                        );
                                    })}
                                </div>
                                <div className="mt-3 text-sm text-right">
                                    <p>Subtotal: {formatPrice(open.subtotal)}</p>
                                    <p>Envío: {formatPrice(open.shipping)}</p>
                                    <p className="font-medium">Total: {formatPrice(open.total)}</p>
                                </div>
                            </div>
                            <div>
                                <p className="text-eyebrow mb-2">Pago: {open.payment_method} · estado: {open.payment_status}</p>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {open.receipt_path && <button onClick={() => openReceipt(open.id)} data-testid="btn-view-receipt" className="px-3 py-1.5 border border-sandLight text-xs uppercase rounded-full hover:border-ink">Ver comprobante</button>}
                                    {[
                                        { v: "paid", label: "Aprobado" },
                                        { v: "verifying", label: "En verificación" },
                                        { v: "rejected", label: "Desaprobado" },
                                    ].map((o) => (
                                        <button key={o.v} onClick={() => updatePayment(open.id, o.v)} disabled={open.payment_status === o.v} data-testid={`btn-payment-${o.v}`} className={`px-3 py-1.5 text-xs uppercase rounded-full transition ${open.payment_status === o.v ? "bg-ink text-bone cursor-default" : "border border-sandLight hover:border-ink"}`}>{o.label}</button>
                                    ))}
                                </div>
                                {open.payment_status === "paid" && <p className="text-[11px] text-ink/50 mt-2">Si aprobaste por error, podés pasarlo a "Desaprobado" o "En verificación" para revertirlo (el pedido vuelve a pendiente).</p>}
                            </div>
                            <div>
                                <p className="text-eyebrow mb-2">Cambiar estado</p>
                                <div className="flex flex-wrap gap-2">
                                    {STATUSES.map((s) => (
                                        <button key={s} onClick={() => updateStatus(open.id, s)} className={`px-3 py-1.5 border text-xs uppercase rounded-full ${open.status === s ? "bg-ink text-bone border-ink" : "border-sandLight hover:border-ink"}`}>{s}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-sandLight flex justify-between items-center">
                            <button onClick={() => deleteOrder(open.id)} data-testid="btn-delete-order" className="px-4 py-2 text-xs uppercase tracking-wide text-red-600 border border-red-200 rounded-full hover:bg-red-50 transition">Eliminar pedido</button>
                            <button onClick={() => setOpen(null)} className="btn-outline">Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
